import React, { useEffect, useMemo, useState } from "react";
import { defineMessages, intlShape, injectIntl } from "react-intl";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import { AnalysisLayerAPI, FieldAPI, Ai360APIModels } from "@ai360/core";

import {
    ErrorDetailsContextMenuLabel,
    ErrorDetailsDialog,
} from "~/action-panel/components/common/error-details-dialog/error-details-dialog";

import { keywords } from "~/action-panel/components/import-module/keywords";
import { Bucket, BucketHeader, Menu } from "~/core";
import ApiResultsWrapper from "~/core/api/components/api-results-wrapper";
import { selectors as picklistSelectors } from "~/core/picklist";
import { getPickListCode, PICKLIST_CROPPING_SEASON } from "~/core/picklist/picklist-names";
import { config as intlConfig } from "~/intl-provider/config";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import { messages as importWizardMessages } from "../../../i18n-messages";

import {
    AnalysisLayerImportStatus,
    FieldStripRecord,
    ProposedAnalysisLayerWithStatus,
} from "../../../models";
import {
    getHasSubmitted,
    getImportFieldStripData,
    getImportFilterListData,
    getProposedAnalysisLayers,
} from "../../../selectors";
import { setHasSubmitted, setProposedAnalysisLayers } from "../../../actions";

const messages = defineMessages({
    ready: {
        id: "importWizard.managementArea.ready",
        defaultMessage: "Ready",
    },
    processing: {
        id: "importWizard.managementArea.processing",
        defaultMessage: "Processing",
    },
    processingError: {
        id: "importWizard.managementArea.processingError",
        defaultMessage: "Processing Error",
    },
    completed: {
        id: "importWizard.managementArea.completed",
        defaultMessage: "Completed",
    },
    error: {
        id: "importWizard.managementArea.error",
        defaultMessage: "Error",
    },
});

const ImportStatusToMessage = {
    [AnalysisLayerImportStatus.Ready]: messages.ready,
    [AnalysisLayerImportStatus.Processing]: messages.processing,
    [AnalysisLayerImportStatus.ProcessingError]: messages.processingError,
    [AnalysisLayerImportStatus.Completed]: messages.completed,
    [AnalysisLayerImportStatus.Error]: messages.error,
};

const ImportStatusClassName: Record<AnalysisLayerImportStatus, string> = {
    [AnalysisLayerImportStatus.Ready]: "import-status-ready",
    [AnalysisLayerImportStatus.Processing]: "import-status-processing",
    [AnalysisLayerImportStatus.ProcessingError]: "import-status-error",
    [AnalysisLayerImportStatus.Completed]: "import-status-ready",
    [AnalysisLayerImportStatus.Error]: "import-status-error",
};

type ManagementAreaPanelProps = {
    updateCanSubmit: (canSubmit: boolean) => void;
    intl: intlShape;
    importAnalysisLayers: boolean;
};

export const ManagementAreaPanel = ({
    updateCanSubmit,
    intl: { formatMessage, formatNumber },
    importAnalysisLayers,
}: ManagementAreaPanelProps) => {
    const dispatch = useAppDispatch();
    const [importAnalysisLayersMutation, result] =
        AnalysisLayerAPI.useImportAnalysisLayersMutation();
    const hasSubmitted = useAppSelector(getHasSubmitted);
    const [analysisLayerGuidWithError, setAnalysisLayerGuidWithError] = useState<string | null>(
        null
    );
    const proposedAnalysisLayers = useAppSelector(
        getProposedAnalysisLayers
    ) as ProposedAnalysisLayerWithStatus[];

    useEffect(
        () => () => {
            dispatch(setProposedAnalysisLayers([]));
        },
        [dispatch]
    );

    const importFilterListaData = useAppSelector(getImportFilterListData);
    const importFileToCroppingSeasonMap = useMemo(() => {
        const map = new Map<string, string>();
        importFilterListaData.croppingSeasons.forEach((cs) => {
            Object.keys(cs.importFiles).forEach((importFileGuid) => {
                map.set(importFileGuid, cs.croppingSeason);
            });
        });
        return map;
    }, [importFilterListaData]);

    const fieldStrips = useAppSelector(getImportFieldStripData) as FieldStripRecord[];
    const fieldGuidImportFileGuidToKey = (fieldGuid, importFileGuid) =>
        `${fieldGuid}_${importFileGuid}`;
    const fieldGuidImportFileGuidToField = useMemo(() => {
        const map = new Map<string, FieldStripRecord>();
        fieldStrips.forEach((strip) => {
            strip.importFileGuidList.forEach((importFile) => {
                map.set(
                    fieldGuidImportFileGuidToKey(strip.fieldGuid, importFile.importFileGuid),
                    strip
                );
            });
        });
        return map;
    }, [fieldStrips]);

    const [statusMap, setStatusMap] = useState<Map<string, AnalysisLayerImportStatus>>(new Map());
    useEffect(() => {
        const newProposedAnalysisLayers = fieldStrips
            .filter((strip) => !strip.noMatch)
            .map((strip) => {
                return strip.importFileGuidList.map((importFile) => {
                    const croppingSeasonYear = importFileToCroppingSeasonMap.get(
                        importFile.importFileGuid
                    );
                    const croppingSeason = croppingSeasons.find(
                        (cs) => cs.label === croppingSeasonYear
                    );
                    return {
                        fieldGuid: strip.fieldGuid,
                        importFileGuid: importFile.importFileGuid,
                        croppingSeasonGuid: croppingSeason?.value,
                        analysisLayerGuid: uuid(),
                        status: AnalysisLayerImportStatus.Ready,
                    };
                });
            })
            .flat();
        const newStatusMap = new Map<string, AnalysisLayerImportStatus>();
        newProposedAnalysisLayers.forEach((layer) => {
            newStatusMap.set(layer.analysisLayerGuid, AnalysisLayerImportStatus.Ready);
        });
        setStatusMap(newStatusMap);
        dispatch(setProposedAnalysisLayers(newProposedAnalysisLayers));
    }, [fieldStrips, dispatch]);

    const croppingSeasons = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROPPING_SEASON)
            ) as FieldAPI.ISeason[]
    );

    useEffect(() => {
        updateCanSubmit(proposedAnalysisLayers.length > 0);
    }, [proposedAnalysisLayers]);

    const removeAnalysisLayer = (index) => {
        const newProposedAnalysisLayers = [
            ...proposedAnalysisLayers.slice(0, index),
            ...proposedAnalysisLayers.slice(index + 1),
        ];
        if (newProposedAnalysisLayers.length !== 0) {
            dispatch(setProposedAnalysisLayers(newProposedAnalysisLayers));
        }
    };

    useEffect(() => {
        if (importAnalysisLayers && !hasSubmitted) {
            dispatch(setHasSubmitted(true));
            const newProposedAnalysisLayers = proposedAnalysisLayers.map((layer) => ({
                ...layer,
                status: AnalysisLayerImportStatus.Processing,
            }));
            dispatch(setProposedAnalysisLayers(newProposedAnalysisLayers));
            importAnalysisLayersMutation(
                newProposedAnalysisLayers.map(
                    ({ status, ...rest }) => rest as AnalysisLayerAPI.ProposedAnalysisLayer
                )
            );
        }
    }, [importAnalysisLayers, proposedAnalysisLayers, hasSubmitted, dispatch]);

    useEffect(() => {
        if (result.error) {
            const newProposedAnalysisLayers = proposedAnalysisLayers.map((layer) => ({
                ...layer,
                status: AnalysisLayerImportStatus.ProcessingError,
            }));
            dispatch(setProposedAnalysisLayers(newProposedAnalysisLayers));
        }
    }, [result]);

    const getFieldAndFarmDisplayText = (fieldName: string, farmName: string) =>
        [farmName, fieldName].filter(Boolean).join(" - ");

    return (
        <div className="management-area-panel">
            <div className="management-area-section">
                <Bucket
                    className="import-field-section-bucket"
                    isExpanded={true}
                    isCollapsible={false}
                    showSymbol={false}
                >
                    <BucketHeader className="import-field-section-header create-management-area-section-header">
                        <div className="create-management-area-header">
                            {formatMessage(importWizardMessages.importWizardCreateLayersText, {
                                count: proposedAnalysisLayers.length,
                            })}
                        </div>
                    </BucketHeader>
                    <div className="field-section-content">
                        {proposedAnalysisLayers.map((proposedLayer, index) => {
                            const contextMenuItems = [
                                {
                                    key: 0,
                                    label: formatMessage(importWizardMessages.removeText),
                                    disabled: proposedAnalysisLayers.length === 1,
                                    action: () => removeAnalysisLayer(index),
                                },
                            ];

                            if (
                                [AnalysisLayerImportStatus.ProcessingError].includes(
                                    proposedLayer.status
                                )
                            ) {
                                contextMenuItems.push({
                                    key: 1,
                                    label: formatMessage(ErrorDetailsContextMenuLabel),
                                    action: () =>
                                        setAnalysisLayerGuidWithError(
                                            proposedLayer.analysisLayerGuid
                                        ),
                                    disabled: false,
                                });
                            }

                            const fieldStrip = fieldGuidImportFileGuidToField.get(
                                fieldGuidImportFileGuidToKey(
                                    proposedLayer.fieldGuid,
                                    proposedLayer.importFileGuid
                                )
                            );
                            const polygonCount =
                                fieldStrip.importFileGuidList.find(
                                    (i) => i.importFileGuid === proposedLayer.importFileGuid
                                )?.pointCount || 0;
                            const displayDetails = [
                                fieldStrip.customerName,
                                getFieldAndFarmDisplayText(
                                    fieldStrip.fieldName,
                                    fieldStrip.farmName
                                ),
                                formatMessage(importWizardMessages.acresText, {
                                    acres: formatNumber(
                                        fieldStrip.acres,
                                        intlConfig.numberFormatOptions
                                    ),
                                }),
                            ].join(", ");

                            const statusClasses = ImportStatusClassName[proposedLayer.status];
                            const statusStr = formatMessage(
                                ImportStatusToMessage[proposedLayer.status]
                            );
                            const importStatus =
                                proposedLayer.status === AnalysisLayerImportStatus.Ready
                                    ? formatMessage(importWizardMessages.eventStripStatusText, {
                                          pointCount: polygonCount,
                                          statusStr,
                                      })
                                    : statusStr;
                            const croppingSeason = importFileToCroppingSeasonMap.get(
                                proposedLayer.importFileGuid
                            );

                            return (
                                <div key={proposedLayer.analysisLayerGuid} className="ifb-row">
                                    <div className="ifb-strip">
                                        <div className="field-selected-column">
                                            <div className="field-selected-div"></div>
                                        </div>
                                        <div className="field-strip-content">
                                            <div className="field-strip-content-row">
                                                <div
                                                    className="farm-field-text"
                                                    title={keywords.MANAGEMENT_AREA}
                                                >
                                                    {keywords.MANAGEMENT_AREA}
                                                </div>
                                                <div className="season">{croppingSeason}</div>
                                            </div>
                                            <div className="field-strip-content-row">
                                                <div
                                                    className="customer-name"
                                                    title={displayDetails}
                                                >
                                                    {displayDetails}
                                                </div>
                                                <div
                                                    className={classNames(
                                                        "feature-count",
                                                        statusClasses
                                                    )}
                                                >
                                                    {importStatus}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="field-context-menu-container">
                                            <Menu
                                                className="field-context-menu context-menu"
                                                isDotMenu={true}
                                                menuItems={contextMenuItems}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Bucket>
            </div>
            {analysisLayerGuidWithError && (
                <ErrorDetailsDialog
                    onClose={() => setAnalysisLayerGuidWithError(null)}
                    correlationId={analysisLayerGuidWithError}
                    logType="createanalysis"
                />
            )}
            {result?.error && <ApiResultsWrapper />}
        </div>
    );
};

export default injectIntl(ManagementAreaPanel);
